import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validateVerizonStatus } from '../lib/verizonValidation';
import styles from '../Series3App.module.scss';
import analytics from '../lib/analytics';
import { lookupAndIdentifyUser } from '../lib/analytics/identifyUser';
import { createSessionFromExistingCredentials } from '../lib/authentication';
import { getRemoteConfig } from '../lib/fi-api/configApi';
import { getFiGrowthBook } from '../lib/growthbook';
import { configActions } from '../reducers/config';
import * as types from '../types';
import { useLocation } from 'react-router-dom';
import { useLocale, apiLanguageToFiLanguage, apiRegionToFiCountry } from './LocaleContext';

// Turning on MAINTENANCE_MODE will display the message below on all page requeusts.
// No other content will be rendered, and users will not be able to checkout.
const MAINTENANCE_MODE = false;
const MAINTENANCE_MODE_MESSAGE = 'The Fi store is temporarily down for maintenance. Check back soon.';

// Capture enablePasswordless from URL immediately at module load time, before React Router can redirect
// This ensures we don't lose the query string during navigation
// Guard against SSR/build environments where window is undefined
if (typeof window !== 'undefined') {
  const initialUrlParams = new URLSearchParams(window.location.search);
  if (initialUrlParams.get('enablePasswordless') === 'true') {
    sessionStorage.setItem('enablePasswordless', 'true');
  }
}

interface Series3ContextType {
  appInitialized: boolean;
  initializing: boolean;
  disablePromoBarInCart: boolean;
  initializationError?: string;
  retryInitialization: () => void;
  isVerizon?: boolean;
  isVerizonLoading?: boolean;
}

const Series3Context = React.createContext<Series3ContextType>({
  appInitialized: false,
  retryInitialization: () => {},
  initializing: false,
  disablePromoBarInCart: false,
  isVerizon: false,
});

function MaintenanceModeMessage() {
  return (
    <div className={styles.container}>
      <div style={{ textAlign: 'center', padding: '2rem' }}>{MAINTENANCE_MODE_MESSAGE}</div>
    </div>
  );
}

interface Series3ContextProviderProps {
  children: React.ReactNode;
  /**
   * If true, we will fetch the remote config with the upgradeFlow parameter set to true. The upgrade flow can have
   * different product attributes (e.g. waived activation fee)
   */
  upgradeFlow?: boolean;
}

export function Series3ContextProvider({ children, upgradeFlow }: Series3ContextProviderProps) {
  const dispatch = useDispatch();

  // We should re-initialize the app if we previously loaded products outside the upgrade flow and now are inside
  // the upgrade flow, or vice versa.
  const appInitialized = useSelector(
    (state: types.AppState) => !!state.config.loaded && !!upgradeFlow === state.config.upgradeFlow,
  );
  const [initializing, setInitializing] = useState<boolean>(false);
  const [initializationError, setInitializationError] = useState<string | undefined>(undefined);

  const location = useLocation();
  const disablePromoBarInCart = useRef<boolean>(false);
  const internalTester = useSelector((state: types.AppState) => state.config.siteConfig.internalTester);
  const [isVerizon, setIsVerizon] = useState<boolean>(false);
  const { country, setLanguage, setCountry } = useLocale();

  useEffect(() => {
    if (location.pathname.includes('redeem') || location.pathname.includes('vip')) {
      disablePromoBarInCart.current = true;
    }
  }, [location]);

  // This callback executes initial app load, or can be executed on-demand in case there was an error and we present
  // user with a UI element to retry.
  const initialize = useCallback(() => {
    if (initializing) {
      return;
    }

    setInitializing(true);
    setInitializationError(undefined);

    // Determine passwordless checkout mode:
    // 1. URL param override (for testing): ?enablePasswordless=true/false
    // 2. SessionStorage (persists URL param across navigation)
    // 3. Growthbook experiment (for A/B testing rollout)
    const urlParams = new URLSearchParams(window.location.search);
    let enablePasswordless = urlParams.get('enablePasswordless') === 'true';
    let disablePasswordless = urlParams.get('enablePasswordless') === 'false';

    // Persist URL param to sessionStorage, or read from sessionStorage if previously set
    if (enablePasswordless) {
      sessionStorage.setItem('enablePasswordless', 'true');
    } else if (disablePasswordless) {
      sessionStorage.setItem('enablePasswordless', 'false');
    } else {
      const storedValue = sessionStorage.getItem('enablePasswordless');
      if (storedValue === 'true') {
        enablePasswordless = true;
      } else if (storedValue === 'false') {
        disablePasswordless = true;
      } else {
        // No URL param or sessionStorage override - check Growthbook experiment
        enablePasswordless = getFiGrowthBook().getFeatureValue<boolean>('passwordless-checkout', false);
        if (enablePasswordless) {
          // Persist to sessionStorage so it stays consistent throughout the checkout flow
          sessionStorage.setItem('enablePasswordless', 'true');
        }
      }
      // Note: If no sessionStorage value, we'll check Growthbook AFTER features are loaded below
    }

    // Track whether we need to check Growthbook after features load
    const needsGrowthbookCheck = !enablePasswordless && !disablePasswordless;

    // It's possible that the cookie for the anonymous ID has not yet been set, so we need to get it directly from
    // Segment analytics instance
    let anonymousId: string | undefined;
    if (window.analytics && typeof window.analytics.user === 'function') {
      anonymousId = window.analytics.user().anonymousId();
    }

    // These things are required for us to render the app:
    // 1) determining whether or not the user is logged in
    // 2) fetching the remote config from our api which includes products and other app settings
    // 3) determining whether the user has access to Verizon pages
    // Note: Growthbook features are already loaded by FeaturesReady wrapper before this component mounts
    Promise.all([
      createSessionFromExistingCredentials(),
      getRemoteConfig({
        upgradeFlow: !!upgradeFlow,
        anonymousId,
        countryCode: country.toString(),
      }),
      validateVerizonStatus(location, internalTester),
    ])
      .then(([session, remoteConfig, verizonValidation]) => {
        // If the user is authenticated and has a stored language/region preference,
        // apply it so the site reflects the language they chose in the app.
        if (session) {
          const userLanguage = apiLanguageToFiLanguage(session.language);
          if (userLanguage) {
            setLanguage(userLanguage);
          }
          const userRegion = apiRegionToFiCountry(session.region);
          if (userRegion) {
            setCountry(userRegion);
          }
        }

        // Most code currently relies on the config being set in the redux store.
        // TODO: Move away from storing the config in Redux and just keep it in state here, then components can
        // can get it from the context. When initializing the Redux store from browser local storage, we currently
        // overwrite the persisted site config with the initial/empty state to make sure it's refreshed upon app
        // load anyways.

        // Determine passwordless checkout setting:
        // - URL param or sessionStorage override takes precedence
        // - Otherwise check Growthbook experiment (features are now loaded)
        let finalPasswordlessEnabled = enablePasswordless;
        if (needsGrowthbookCheck) {
          finalPasswordlessEnabled = getFiGrowthBook().getFeatureValue<boolean>('passwordless-checkout', false);
          // Persist to sessionStorage so assignment stays consistent within this session
          sessionStorage.setItem('enablePasswordless', finalPasswordlessEnabled ? 'true' : 'false');
        }

        if (finalPasswordlessEnabled) {
          remoteConfig.config.passwordlessCheckoutEnabled = true;
        } else if (disablePasswordless) {
          remoteConfig.config.passwordlessCheckoutEnabled = false;
        }

        dispatch(configActions.setConfig(remoteConfig, !!upgradeFlow));

        // Reset anonymous ID, but preserve user ID (by re-identifying).
        if (remoteConfig.config.shouldResetAnonymousId) {
          analytics.reset();

          if (session) {
            return lookupAndIdentifyUser(session).catch(() => {
              // This is just for analytics so if we weren't able to re-identify the user, we can still continue.
            });
          }
        }

        setIsVerizon(verizonValidation ?? false);
      })
      .catch((error) => {
        setInitializationError(error.message);
      })
      .finally(() => {
        setInitializing(false);
      });
  }, [dispatch, initializing, upgradeFlow, location, internalTester, country, setLanguage, setCountry]);

  // Initialize on mount
  useEffect(() => {
    if (MAINTENANCE_MODE || initializing) {
      return;
    }

    if (!initializationError && !appInitialized) {
      initialize();
    }
  }, [appInitialized, initializationError, initialize, initializing]);

  if (MAINTENANCE_MODE) {
    return <MaintenanceModeMessage />;
  }

  return (
    <Series3Context.Provider
      value={{
        appInitialized,
        initializing,
        initializationError,
        disablePromoBarInCart: disablePromoBarInCart.current,
        isVerizon,
        retryInitialization: initialize,
      }}
    >
      {children}
    </Series3Context.Provider>
  );
}

export function useSeries3Context() {
  return useContext(Series3Context);
}

export default Series3Context;
